/**
 * Reglas del calendario judicial venezolano.
 * Port fiel de includes/functions.php
 *
 * Vive en `packages/dominio` (antes `artifizio-api/src/lib/venezuelan-calendar.ts`,
 * que ahora sólo re-exporta este fichero) porque es la parte PÚBLICA del cómputo:
 * la usan el Worker y la app. Ver el README del paquete antes de añadir nada aquí.
 */

/**
 * Opciones de jurisdicción para los predicados del calendario.
 *
 * `penal: true` significa que el cómputo corre en sede penal, donde
 * **NO existe el receso judicial de agosto a septiembre**. El receso
 * del 15-ago al 15-sep es una institución del proceso civil (Art. 201
 * CPC); los tribunales penales despachan esos días por el régimen de
 * guardia permanente de los circuitos judiciales penales (Art. 156
 * COPP). El receso de fin de año (24-dic → 6-ene) SÍ aplica también
 * en penal y no cambia.
 *
 * El default (`penal` ausente o `false`) preserva el comportamiento
 * histórico civil de todas estas funciones.
 */
/**
 * El calendario nacional tal y como lo sirve `feriados_nacionales`.
 *
 * `fijos` son claves 'M-D' SIN ceros a la izquierda (igual que FERIADOS_FIJOS)
 * y `moviles` los cuatro tipos que se derivan de Pascua. Se pasan como conjunto
 * y no como lista de fechas porque el módulo sigue siendo puro: aquí no se sabe
 * qué años va a preguntar quien llame.
 */
export interface CalendarioNacional {
  fijos: ReadonlySet<string>;
  moviles: ReadonlySet<TipoFeriadoMovil>;
}

export type TipoFeriadoMovil = 'carnaval_lunes' | 'carnaval_martes' | 'jueves_santo' | 'viernes_santo';

export interface OpcionesJurisdiccion {
  /** true = jurisdicción penal (sin receso de agosto-septiembre). */
  penal?: boolean;
  /**
   * true = el cómputo NO observa receso judicial alguno, ni siquiera el de
   * fin de año.
   *
   * Es para el tiempo de pena cumplida. Un receso judicial suspende los
   * LAPSOS PROCESALES —nadie puede actuar en el expediente—, pero no suspende
   * la condena: un penado está privado de libertad el 25 de diciembre igual
   * que cualquier otro día. El Art. 476 COPP lo dice sin rodeos al fijar la
   * base de cualquier beneficio: «sólo se tomará en cuenta el tiempo que
   * hubiere estado efectivamente privado de su libertad».
   *
   * Sin esta opción, una condena de cuatro años se alargaba unas cuatro
   * semanas y una de treinta, más de un año — sólo por Navidades.
   */
  sinReceso?: boolean;
  /**
   * Feriados DECRETADOS que el operador dio de alta en `feriados_decretados`
   * (migración 73), en formato 'YYYY-MM-DD'.
   *
   * Se inyectan porque este módulo es puro y síncrono a propósito —no puede
   * leer la base— y la tabla existía desde mayo sin que nadie la consultara: el
   * operador decretaba un día no laborable, el panel se lo devolvía en la lista,
   * y el motor seguía contándolo como hábil. Todo lapso que cruzara ese día
   * quedaba corrido un día.
   *
   * Quien calcula los carga UNA vez por invocación —igual que `insertarHitos`
   * precarga `dias_despacho`— y los pasa por aquí. Ausente = ninguno, que es el
   * comportamiento de siempre y el que usan las pruebas.
   */
  feriadosDecretados?: ReadonlySet<string>;
  /**
   * El calendario NACIONAL vigente, leído de `feriados_nacionales` (mig 73).
   *
   * Ausente = se usan las listas de este módulo, que es lo que el motor hizo
   * desde siempre. Presente = manda la tabla, que es la que el operador puede
   * corregir sin desplegar. Las dos coinciden hoy día por día: la migración 120
   * desactivó la única fila que sobraba.
   */
  calendarioNacional?: CalendarioNacional;
}

// ════════════════════════════════════════════════════════════════════════════
// EL DÍA DE HOY, EN VENEZUELA
// ════════════════════════════════════════════════════════════════════════════
//
// Cloudflare Workers corre en UTC. Venezuela es UTC−04:00 todo el año, sin
// horario de verano. Por tanto `new Date().toISOString().slice(0,10)` devuelve
// la fecha del DÍA SIGUIENTE entre las 20:00 y la medianoche de Venezuela —
// cuatro horas de cada día.
//
// Eso no es un detalle cosmético en esta aplicación. Auditoría del 28-08-2026:
// el barrido de vencidos del cron (`.lt('fecha_fin', hoy)`) clasificaba como
// VENCIDO un lapso que vencía ese mismo día y seguía abierto, y mandaba al
// abogado un correo y un push diciendo «Plazo VENCIDO» la tarde del día en que
// todavía podía actuar. La fila queda escrita en `notificaciones` con
// `tipo='vencido'` y, marcada como enviada, nunca se corrige sola.
//
// Este fichero ya llevaba la advertencia (AUDIT-402, ver `formatDate` abajo)
// pero sólo se había aplicado DENTRO del motor. Estas dos funciones son para
// todo lo que le pasa una fecha desde fuera: rutas, cron, validaciones.
//
// `Intl` con `timeZone` es el idioma correcto y el que ya usa `tasa-bcv.ts`:
// no asume el desfase, lo pregunta. Si Venezuela volviera a cambiar de huso
// —ya lo hizo en 2007 y en 2016— esto sigue siendo correcto sin tocarlo.

/** Zona horaria oficial de Venezuela. Un solo sitio la nombra. */
export const ZONA_VE = 'America/Caracas';

/**
 * La fecha de HOY en Venezuela, como 'YYYY-MM-DD'.
 *
 * Úsala en lugar de `new Date().toISOString().slice(0,10)` en TODO lo que
 * decida qué día es: valores por defecto de `fecha_real`, comparaciones de
 * vencimiento, validaciones de «no puede ser futura».
 */
export function hoyVE(ahora: Date = new Date()): string {
  // 'en-CA' formatea como YYYY-MM-DD, que es exactamente lo que guarda la base.
  return new Intl.DateTimeFormat('en-CA', { timeZone: ZONA_VE }).format(ahora);
}

/** El mes en curso en Venezuela, como 'YYYY-MM'. Para los selectores de mes. */
export function mesVE(ahora: Date = new Date()): string {
  return hoyVE(ahora).slice(0, 7);
}

/**
 * La fecha de Venezuela dentro de `dias` días (negativo = hacia atrás).
 *
 * Se calcula sobre el CALENDARIO, no sumando milisegundos a un instante: sumar
 * `n * 86400000` a `Date.now()` y luego formatear vuelve a mezclar husos y
 * reintroduce el mismo desfase por otro camino.
 */
export function fechaVEMas(dias: number, ahora: Date = new Date()): string {
  const [y, m, d] = hoyVE(ahora).split('-').map(Number);
  // Mediodía UTC: inmune a cualquier desfase al hacer aritmética de días.
  const base = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  base.setUTCDate(base.getUTCDate() + dias);
  return base.toISOString().slice(0, 10);
}

/** Vacaciones judiciales: 15 ago - 15 sep (sólo civil), 24 dic - 6 ene */
export function esVacacionJudicial(
  fecha: string,
  opts: OpcionesJurisdiccion = {},
): boolean {
  // El tiempo de pena cumplida no conoce recesos (Art. 476 COPP).
  if (opts.sinReceso) return false;
  const d = new Date(fecha + 'T12:00:00');
  const m = d.getMonth() + 1;
  const day = d.getDate();
  // Receso de agosto-septiembre (Art. 201 CPC): NO corre en penal.
  if (!opts.penal && ((m === 8 && day >= 15) || (m === 9 && day <= 15))) return true;
  // Receso de fin de año: aplica en todas las jurisdicciones.
  if ((m === 12 && day >= 24) || (m === 1 && day <= 6)) return true;
  return false;
}

/** Fin de semana (sábado=6, domingo=0) */
export function esFinDeSemana(fecha: string): boolean {
  const d = new Date(fecha + 'T12:00:00');
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

/**
 * Calcula días de Pascua para un año dado (algoritmo de Meeus/Jones/Butcher).
 * Retorna Date del Domingo de Pascua. Memoizado por año.
 */
const EASTER_CACHE: Record<number, Date> = {};
function easterDate(year: number): Date {
  const cached = EASTER_CACHE[year];
  if (cached) return cached;
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  const result = new Date(year, month - 1, day, 12, 0, 0);
  EASTER_CACHE[year] = result;
  return result;
}

/**
 * Cache de feriados móviles por año como timestamps (ms a mediodía).
 * Evita recalcular Pascua y aritmética de fechas en cada llamada.
 */
const FERIADOS_MOVILES_CACHE: Record<number, Array<[TipoFeriadoMovil, number]>> = {};
function obtenerFeriadosMoviles(year: number): Array<[TipoFeriadoMovil, number]> {
  const cached = FERIADOS_MOVILES_CACHE[year];
  if (cached) return cached;
  // AUDIT-401: usamos setUTCDate en lugar de aritmética en
  // milisegundos (`easterMs - N * 86400000`). La aritmética en ms
  // es frágil bajo runtimes con DST porque cruzar el cambio de
  // offset entre Easter y un feriado posterior introduce ±1 hora
  // de drift, lo que rompe `moviles.includes(fechaMs)`. Carnaval
  // 2026 (NYC) manifestó el bug en Fase 0.4. Aunque sólo Carnaval
  // cruza DST con el calendario actual, el patrón es estructural
  // mente frágil y se corrige en los 4 feriados móviles a la vez
  // (consulta-pendiente-3 opción A, 2026-05-16).
  const easter = easterDate(year);
  const offset = (days: number): number => {
    const d = new Date(easter);
    d.setUTCDate(d.getUTCDate() - days);
    return d.getTime();
  };
  // Con NOMBRE, no sólo el instante: desde la migración 73 el operador puede
  // desactivar uno solo (el calendario nacional es una tabla), y para honrar eso
  // hay que saber cuál es cuál.
  const moviles: Array<[TipoFeriadoMovil, number]> = [
    ['carnaval_lunes', offset(48)],
    ['carnaval_martes', offset(47)],
    ['jueves_santo', offset(3)],
    ['viernes_santo', offset(2)],
  ];
  FERIADOS_MOVILES_CACHE[year] = moviles;
  return moviles;
}

// Feriados fijos como Set de "mes-día" para lookup O(1)
const FERIADOS_FIJOS = new Set([
  '1-1',   // Año nuevo
  '4-19',  // Declaración de Independencia
  '5-1',   // Día del trabajador
  '6-24',  // Batalla de Carabobo
  '7-5',   // Día de la independencia
  '7-24',  // Natalicio de Bolívar
  '10-12', // Día de la resistencia indígena
  '12-25', // Navidad
]);

/**
 * Feriados nacionales de Venezuela (fijos + móviles).
 *
 * Si el llamante trae `calendarioNacional` —lo carga el motor desde la tabla
 * `feriados_nacionales`— manda ése; si no, las listas de este módulo. La tabla
 * existía desde mayo y no la leía nadie: el operador podía darle de baja a un
 * feriado y todo lapso que lo cruzara lo seguía saltando igual.
 */
export function esFeriadoNacional(fecha: string, opts: OpcionesJurisdiccion = {}): boolean {
  const d = new Date(fecha + 'T12:00:00');
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const y = d.getFullYear();

  const fijos = opts.calendarioNacional?.fijos ?? FERIADOS_FIJOS;
  if (fijos.has(`${m}-${day}`)) return true;

  // Feriados móviles basados en Pascua (cacheados por año)
  const fechaMs = new Date(y, m - 1, day, 12, 0, 0).getTime();
  const activos = opts.calendarioNacional?.moviles;
  return obtenerFeriadosMoviles(y)
    .some(([tipo, ms]) => ms === fechaMs && (!activos || activos.has(tipo)));
}

/** Verifica si un día es hábil (no feriado, no fin de semana, no vacación) */
export function esDiaHabil(fecha: string, opts: OpcionesJurisdiccion = {}): boolean {
  // Un feriado decretado por el Ejecutivo pesa igual que uno de la lista fija:
  // ese día no hay despacho. Se comprueba primero porque es lo más barato.
  if (opts.feriadosDecretados?.has(fecha)) return false;
  return !esFinDeSemana(fecha) && !esFeriadoNacional(fecha, opts) && !esVacacionJudicial(fecha, opts);
}

/** Siguiente día hábil (Art. 200 CPC).
 *  AUDIT-403 (Fase 2E): contador de seguridad como defensive coding.
 *  esDiaHabil siempre converge en ≤14 iteraciones bajo cualquier
 *  estado realista de la tabla de feriados (worst case: vacación
 *  judicial de invierno 24-dic → 6-ene = 14 días). El cap de 60 da
 *  4× ese tramo. Si nunca converge, la tabla de feriados está
 *  corrupta — fail-fast en lugar de loop infinito. */
export function siguienteDiaHabil(
  fecha: string,
  opts: OpcionesJurisdiccion = {},
): string {
  let d = new Date(fecha + 'T12:00:00');
  let safety = 60;
  do {
    d.setDate(d.getDate() + 1);
    safety--;
    if (safety <= 0) {
      throw new Error(
        `siguienteDiaHabil: no convergence after 60 iterations from ${fecha}. ` +
        `Tabla de feriados/vacaciones probablemente corrupta — revisar ` +
        `obtenerFeriadosMoviles, esFeriadoNacional, esVacacionJudicial.`,
      );
    }
  } while (!esDiaHabil(formatDate(d), opts));
  return formatDate(d);
}

/** Formatea Date a YYYY-MM-DD respetando timezone local.
 *  AUDIT-402 (Fase 2E): NO usar `toISOString().slice(0,10)` que
 *  retorna fecha UTC y bajo TZ=America/Caracas o America/New_York
 *  produce off-by-one cuando la hora local cae cerca de medianoche
 *  UTC. Para el motor, las fechas son fechas del calendario local
 *  (Venezuela) sin componente horario. `vitest.config.ts` fuerza
 *  `TZ=UTC` para que la suite coincida con producción (CF Workers
 *  corre UTC); igualmente este helper queda TZ-safe por defensive
 *  coding.
 */
export function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Obtiene último día del mes */
export function lastDayOfMonth(yearMonth: string): string {
  const [y, m] = yearMonth.split('-').map(Number);
  const d = new Date(y, m, 0);
  return formatDate(d);
}

/**
 * Estima N días de despacho a partir de una fecha.
 * Usado como fallback cuando no hay suficientes días registrados.
 */
export function estimarDiasDespacho(
  desde: string,
  dias: number,
  opts: OpcionesJurisdiccion & { suspensiones?: Array<{ inicio: string; fin: string }> } = {},
): string {
  if (dias <= 0) return desde;
  let d = new Date(desde + 'T12:00:00');
  let count = 0;
  const susp = opts.suspensiones ?? [];
  // Tope defensivo: sin él, un conjunto de suspensiones que cubra el horizonte
  // deja este bucle girando para siempre. Con 4× el lapso más 500 días hay
  // margen de sobra para vacaciones, feriados y suspensiones razonables.
  let safety = dias * 4 + 500;
  while (count < dias && safety > 0) {
    d.setDate(d.getDate() + 1);
    safety--;
    const f = formatDate(d);
    // La suspensión convencional del Art. 202 CPC no distingue tipo de día:
    // mientras corre, no corre el lapso (decisión D-15).
    if (estaSuspendidoInline(f, susp)) continue;
    if (esDiaHabil(f, opts)) {
      count++;
    }
  }
  if (count < dias) {
    throw new Error(
      `estimarDiasDespacho: no convergió (¿las suspensiones cubren el horizonte?) desde=${desde} dias=${dias}`,
    );
  }
  return formatDate(d);
}

/**
 * Cuenta N días continuos (calendario) saltando vacaciones judiciales.
 *
 * DT-17 (Fase 4A): el parámetro opcional `suspensiones` descuenta
 * los días que caen dentro de intervalos de suspensión convencional
 * Art. 202 CPC (D-15). Si no se pasa, comportamiento legacy.
 */
export function contarDiasContinuos(
  desde: string,
  dias: number,
  opts: OpcionesJurisdiccion & { suspensiones?: Array<{ inicio: string; fin: string }> } = {},
): string {
  if (dias <= 0) return desde;
  let d = new Date(desde + 'T12:00:00');
  let count = 0;
  let safety = (dias * 4) + 500; // cap defensivo amplio (suspensiones + vacación)
  const susp = opts.suspensiones ?? [];
  while (count < dias && safety > 0) {
    d.setDate(d.getDate() + 1);
    safety--;
    const fecha = formatDate(d);
    if (esVacacionJudicial(fecha, opts)) continue;
    if (susp.length > 0 && estaSuspendidoInline(fecha, susp)) continue;
    count++;
  }
  if (safety <= 0) {
    throw new Error(`contarDiasContinuos: no convergence (suspensiones cubren todo el rango?) desde=${desde} dias=${dias}`);
  }
  return formatDate(d);
}

/** Predicado interno: ¿fecha cae dentro de alguna suspensión? */
function estaSuspendidoInline(
  fecha: string,
  suspensiones: Array<{ inicio: string; fin: string }>,
): boolean {
  for (const s of suspensiones) {
    if (fecha >= s.inicio && fecha <= s.fin) return true;
  }
  return false;
}

/**
 * Vencimiento de un lapso fijado en MESES (Art. 199 CPC y Art. 12 CC).
 *
 * Las dos normas dicen lo mismo y son la misma regla:
 *
 *   Art. 12 CC — «Los lapsos de años o meses se contarán desde el día
 *     siguiente al de la fecha del acto que da lugar al lapso, y concluirán el
 *     día de fecha igual a la del acto, del año o mes que corresponda para
 *     completar el número del lapso. El lapso que, según la regla anterior,
 *     debiera cumplirse en un día de que carezca el mes, se entenderá vencido
 *     el último de ese mes.»
 *
 * Es decir: el vencimiento es el día de FECHA IGUAL del mes que toque, y si
 * ese mes no tiene ese día, el último. No es una cuenta de días.
 *
 * Por qué existe esta función: el motor convertía «seis meses» del Art. 295
 * COPP en 180 días continuos. Seis meses desde el 31 de marzo vencen el 30 de
 * septiembre; 180 días caen el 27. Tres días de diferencia en el lapso del que
 * depende el archivo de pleno derecho de una investigación penal.
 *
 * No descuenta receso ni feriados: un lapso por meses corre por calendario. Si
 * el vencimiento cae en día no laborable, quien lo prorroga es el Art. 200 CPC
 * (la `fecha_acto_practicable` del motor), no este cómputo.
 */
export function contarMesesComoFecha(desde: string, meses: number): string {
  if (meses <= 0) return desde;
  const [y, m, d] = desde.slice(0, 10).split('-').map(Number);
  const total = (m - 1) + meses;
  const anio = y + Math.floor(total / 12);
  const mes = (total % 12) + 1;                              // 1-12
  const ultimoDia = new Date(Date.UTC(anio, mes, 0)).getUTCDate();
  const dia = Math.min(d, ultimoDia);                        // «el último de ese mes»
  return `${anio}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

/**
 * Suma N horas continuas a un timestamp ISO 8601.
 *
 * Usado para los lapsos constitucionales del COPP (Arts. 234, 373):
 * 12 h aprehensor → MP, 36 h MP → juez de control, 48 h decisión del
 * juez. Las horas continuas NO se descuentan por feriados ni fines de
 * semana — los tribunales penales mantienen guardia 24/7 (Art. 156
 * LOPJ + resoluciones del TSJ sobre régimen de guardia para flagrancia
 * y medidas cautelares).
 *
 * Acepta entrada con o sin componente horario:
 *   - 'YYYY-MM-DD'             → asume 00:00:00 UTC (medianoche)
 *   - 'YYYY-MM-DDTHH:MM[:SS]'  → mantiene el timestamp exacto
 *
 * Retorna en formato ISO 8601 (`YYYY-MM-DDTHH:MM:SS.sssZ`) — el caller
 * decide cómo presentarlo al usuario. Para guardar en `hitos_caso` el
 * caller debe normalizar a `YYYY-MM-DD HH:MM:SS` (timestamp local) o
 * a una sola fecha de día (`YYYY-MM-DD`) según convenga.
 */
export function contarHorasContinuas(desde: string, horas: number): string {
  // Audit 2026-05-04 finding 2.8: separar `horas < 0` (error de datos —
  // no debería ocurrir; surge si hay corrupción o bug del generador)
  // de `horas === 0` (idempotente — devolver `desde` sin cambios).
  // Antes el guard `<= 0` aceptaba ambos silenciosamente, ocultando
  // el bug de corrupción.
  if (horas < 0) {
    throw new Error(`contarHorasContinuas: horas debe ser >= 0 (recibido: ${horas})`);
  }
  if (horas === 0) return desde;
  // Si viene sin componente horario, asumir mediodía (consistente con
  // las otras funciones del módulo) para que la suma de horas no caiga
  // en una zona ambigua respecto a DST.
  const inicio = desde.includes('T')
    ? new Date(desde)
    : new Date(desde + 'T12:00:00');
  const ms = inicio.getTime() + horas * 3600 * 1000;
  return new Date(ms).toISOString();
}

/**
 * Suma N días hábiles penales — Sprint v0.10.0 (10.4).
 *
 * Usado por el Art. 454 COPP: "15 días hábiles" para interponer
 * recurso de casación penal. La SCP/TSJ en Sent. N° 66/30-07-2020
 * (sobre el Art. 454 reformado en violencia de género) fijó que
 * "días hábiles" en sede penal significa excluir SÓLO:
 *   - domingos,
 *   - feriados nacionales,
 *   - el receso judicial de fin de año (24-Dec → 6-Jan).
 *
 * El receso del 15-ago al 15-sep NO se descuenta: es el receso del
 * Art. 201 CPC, propio del proceso civil, y los tribunales penales
 * despachan esos días (Art. 156 COPP + guardia permanente de los
 * circuitos penales). Por eso el conteo pasa `{ penal: true }` a los
 * predicados del calendario.
 *
 * Los sábados se cuentan como hábiles en jurisdicción penal por
 * reforma del TSJ (calendario judicial penal propio, Sent. SC/TSJ
 * N° 111/16-04-2021). Esto distingue al cómputo penal del cómputo
 * civil de `esDiaHabil` (que excluye fines de semana enteros).
 *
 * Si desde cae en un día no hábil, el primer día del lapso es el
 * próximo día hábil (Art. 198 CPC supletoriamente).
 *
 * @param desde  fecha ISO 'YYYY-MM-DD' del acto procesal de origen.
 *               El día `desde` NO se cuenta como día 1 (Art. 198).
 * @param dias   número de días hábiles penales a sumar.
 * @returns      fecha 'YYYY-MM-DD' de vencimiento.
 */
export function contarDiasHabilesPenales(
  desde: string,
  dias: number,
  opts: {
    suspensiones?: Array<{ inicio: string; fin: string }>;
    feriadosDecretados?: ReadonlySet<string>;
    calendarioNacional?: CalendarioNacional;
  } = {},
): string {
  if (dias <= 0) return desde;
  const d = new Date(desde + 'T12:00:00');
  let count = 0;
  let safety = dias * 4 + 100; // límite defensivo ampliado para suspensiones
  const susp = opts.suspensiones ?? [];
  while (count < dias && safety > 0) {
    d.setDate(d.getDate() + 1);
    const f = formatDate(d);
    const dow = d.getDay(); // 0 = domingo
    // Excluir SÓLO: domingo, feriado nacional y receso de fin de año.
    // Los sábados SÍ se cuentan como hábiles en jurisdicción penal, y
    // el receso de agosto-septiembre NO corre en penal.
    if (dow === 0) { safety--; continue; }
    if (esVacacionJudicial(f, { penal: true })) { safety--; continue; }
    if (esFeriadoNacional(f, { calendarioNacional: opts.calendarioNacional })) { safety--; continue; }
    // Un feriado decretado por el Ejecutivo tampoco es hábil en penal.
    if (opts.feriadosDecretados?.has(f)) { safety--; continue; }
    // DT-17 (Fase 4A): suspensiones convencionales Art. 202 CPC
    // (decisión D-15) cubren todos los tipos incluido habiles_penales.
    if (susp.length > 0 && estaSuspendidoInline(f, susp)) { safety--; continue; }
    count++;
    safety--;
  }
  return formatDate(d);
}

/**
 * Término de distancia por ciudad (Art. 205 CPC).
 *
 * AUDIT-405 (Fase 2E): tabla con valores doctrinales SIN fuente
 * documentada formalmente. Los valores son referenciales heredados
 * de práctica forense. La resolución TSJ vinculante sobre términos
 * de distancia post-reforma del CPC NO se ha verificado contra
 * fuente primaria (decisiones.tsj.gob.ve o G.O.).
 *
 * Acción Fase 3 (AUDIT-201): migrar a tabla DB editable por
 * operador con resolución vigente como columna obligatoria.
 *
 * Estado por entrada — todas marcadas [verificar fuente: resolución
 * TSJ pendiente]:
 *   caracas (0): no requiere término (sede del TSJ).
 *   los_teques (1), maracay (2), valencia (2), san_juan (2): práctica
 *     común.
 *   resto: valores históricos heredados sin verificación formal.
 */
export const TERMINO_DISTANCIA_DIAS: Record<string, number> = {
  // [verificar fuente: resolución TSJ AAAA-MM-DD] aplica a TODAS las
  // entradas. Validar contra G.O. antes de uso productivo.
  caracas: 0, // sede TSJ
  los_teques: 1,
  maracay: 2,
  valencia: 2,
  san_juan: 2,
  san_carlos: 3,
  san_felipe: 3,
  barcelona: 4,
  barquisimeto: 4,
  coro: 5,
  guanare: 5,
  cumana: 5,
  la_asuncion: 5,
  san_fernando: 5,
  carupano: 6,
  maturin: 6,
  barinas: 6,
  trujillo: 6,
  ciudad_bolivar: 6,
  merida: 7,
  puerto_ordaz: 8,
  maracaibo: 8,
  san_cristobal: 9,
};
